import type { ExperienceExportResponse } from '@/types/experience-export'

type CellValue = string | number | boolean | null | undefined | string[]

function escapeCell(value: CellValue): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (Array.isArray(value)) return escapeCell(JSON.stringify(value))
  const str = String(value)
  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function buildRow(values: CellValue[]): string {
  return values.map(escapeCell).join(',')
}

function buildCsv(headers: string[], rows: CellValue[][]): string {
  const lines: string[] = [buildRow(headers)]
  for (const row of rows) {
    lines.push(buildRow(row))
  }
  return lines.join('\n')
}

export function sessionRowsToCsv(
  sessions: ExperienceExportResponse['sessions'],
  campaignsLookup: Map<string, ExperienceExportResponse['campaigns'][number]>,
): string {
  const headers = [
    'sessionId',
    'status',
    'campaignId',
    'campaignName',
    'userId',
    'walletId',
    'userEmail',
    'userFirstName',
    'userLastName',
    'userExternalId',
    'periodIndex',
    'roundId',
    'startedAt',
    'completedAt',
    'sessionDurationMs',
    'score',
    'totalStages',
    'totalQuestions',
  ]

  const rows = sessions.map((s) => {
    const campaign = campaignsLookup.get(s.campaignId)
    return [
      s.sessionId,
      s.status,
      s.campaignId,
      campaign?.campaignName ?? s.campaignName,
      s.userId,
      s.walletId,
      s.userEmail,
      s.userFirstName,
      s.userLastName,
      s.userExternalId,
      s.periodIndex,
      s.roundId,
      s.startedAt,
      s.completedAt,
      s.sessionDurationMs,
      s.score,
      s.totalStages,
      s.totalQuestions,
    ] satisfies CellValue[]
  })

  return buildCsv(headers, rows)
}

export function answerRowsToCsv(
  answers: ExperienceExportResponse['answers'],
  sessionsLookup: Map<string, ExperienceExportResponse['sessions'][number]>,
  campaignsLookup: Map<string, ExperienceExportResponse['campaigns'][number]>,
): string {
  const headers = [
    'sessionId',
    'userId',
    'walletId',
    'userEmail',
    'userFirstName',
    'userLastName',
    'userExternalId',
    'sessionStatus',
    'campaignId',
    'campaignName',
    'startedAt',
    'completedAt',
    'sessionDurationMs',
    'score',
    'periodIndex',
    'roundId',
    'stageOrder',
    'sessionStageOrder',
    'questionOrder',
    'questionId',
    'questionText',
    'questionRound',
    'responseType',
    'selectedChoiceId',
    'selectedChoiceIds',
    'selectedChoiceLabel',
    'textResponse',
    'ratingValue',
    'booleanValue',
    'correct',
    'recordedAt',
    'durationFromStartMs',
    'durationFromPrevStageMs',
  ]

  const rows = answers.map((a) => {
    const session = sessionsLookup.get(a.sessionId)
    const campaign = session ? campaignsLookup.get(session.campaignId) : undefined

    return [
      a.sessionId,
      session?.userId ?? null,
      session?.walletId ?? null,
      session?.userEmail ?? null,
      session?.userFirstName ?? null,
      session?.userLastName ?? null,
      session?.userExternalId ?? null,
      session?.status ?? null,
      session?.campaignId ?? null,
      campaign?.campaignName ?? session?.campaignName ?? null,
      session?.startedAt ?? null,
      session?.completedAt ?? null,
      session?.sessionDurationMs ?? null,
      session?.score ?? null,
      session?.periodIndex ?? null,
      session?.roundId ?? null,
      a.stageOrder,
      a.sessionStageOrder,
      a.questionOrder,
      a.questionId,
      a.questionText,
      a.questionRound,
      a.responseType,
      a.selectedChoiceId,
      a.selectedChoiceIds,
      a.selectedChoiceLabel,
      a.textResponse,
      a.ratingValue,
      a.booleanValue,
      a.correct,
      a.recordedAt,
      a.durationFromStartMs,
      a.durationFromPrevStageMs,
    ] satisfies CellValue[]
  })

  return buildCsv(headers, rows)
}
